import React, { useState, useEffect, useRef } from 'react';
import './index.css';
import { FaHeart, FaComment, FaShare, FaPlay, FaPlus, FaSpinner } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';

const DEFAULT_AVATAR = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23a0a0a0'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/></svg>";

const ReelVideo = ({ reel, isActive, onOpenComments }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(reel.is_liked || false);
  const [likesCount, setLikesCount] = useState(reel.likes_count || 0);
  const [showHeart, setShowHeart] = useState(false);

  useEffect(() => {
    if (isActive) {
      videoRef.current?.play().then(() => setIsPlaying(true)).catch(e => { /* console.log('Autoplay blocked', e); */ });
      addView();
    } else {
      videoRef.current?.pause();
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
      setIsPlaying(false);
    }
  }, [isActive]);

  const addView = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reel_id: reel.id.toString()
        })
      });
    } catch (e) {
      // console.error('Error adding view:', e);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    } else {
      videoRef.current?.play();
      setIsPlaying(true);
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    // API call to like would go here
  };

  const handleDoubleClick = () => {
    if (!isLiked) {
      handleLike();
    }
    setShowHeart(true);
    setTimeout(() => setShowHeart(false), 700);
  };

  return (
    <div className="reel-video-container" onDoubleClick={handleDoubleClick}>
      <video
        ref={videoRef}
        className="reel-video-element"
        src={reel.video_url?.startsWith('http') ? reel.video_url : `https://www.whysocial.in/clap-kartel-reels-module/public/${reel.video_url || reel.video}`}
        loop
        playsInline
        onClick={togglePlay}
        poster={reel.thumbnail_url || reel.thumbnail}
      />
      {!isPlaying && (
        <div className="reel-play-icon" onClick={togglePlay}>
          <FaPlay />
        </div>
      )}

      <AnimatePresence>
        {showHeart && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1.2 }}
            exit={{ opacity: 0, scale: 1.5 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              fontSize: '5rem',
              zIndex: 10,
              pointerEvents: 'none'
            }}
          >
            <FaHeart />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right Side Action Panel */}
      <div className="reel-action-panel">
        <div className="reel-action-btn" onClick={handleLike}>
          <FaHeart color={isLiked ? '#e74c3c' : 'white'} size={24} />
          <span>{likesCount}</span>
        </div>
        <div className="reel-action-btn" onClick={() => onOpenComments(reel)}>
          <FaComment color="white" size={24} />
          <span>{reel.comments_count || 0}</span>
        </div>
        <div className="reel-action-btn">
          <FaShare color="white" size={24} />
          <span>Share</span>
        </div>
      </div>

      {/* Bottom Info Panel */}
      <div className="reel-bottom-info">
        <h3 className="reel-title">@{reel.uploader_name || reel.user_name || 'anonymous'}</h3>
        {reel.title && reel.title.trim().toLowerCase() !== reel.description?.trim().toLowerCase() && (
          <p className="reel-video-title" style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: '600', textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
            {reel.title}
          </p>
        )}
        <p className="reel-description">{reel.description}</p>
        <div className="reel-meta">
          <span>{reel.views_count || 0} views</span>
        </div>
      </div>
    </div>
  );
};

const Reel = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeReelIndex, setActiveReelIndex] = useState(0);
  const containerRef = useRef(null);

  const activeReelIndexRef = useRef(0);
  const reelsLengthRef = useRef(0);

  useEffect(() => {
    activeReelIndexRef.current = activeReelIndex;
  }, [activeReelIndex]);

  useEffect(() => {
    reelsLengthRef.current = reels.length;
  }, [reels]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isScrolling = false;
    let scrollTimeout = null;

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) < 10) return;
      
      e.preventDefault();
      if (isScrolling) return;

      const { clientHeight } = container;
      const currentIndex = activeReelIndexRef.current;
      const totalReels = reelsLengthRef.current;
      
      let targetIndex = currentIndex;
      if (e.deltaY > 0) {
        if (currentIndex < totalReels - 1) {
          targetIndex = currentIndex + 1;
        }
      } else if (e.deltaY < 0) {
        if (currentIndex > 0) {
          targetIndex = currentIndex - 1;
        }
      }

      if (targetIndex !== currentIndex) {
        isScrolling = true;
        container.scrollTo({
          top: targetIndex * clientHeight,
          behavior: 'smooth'
        });
        
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          isScrolling = false;
        }, 600);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  // Comments Modal State
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [activeReelForComments, setActiveReelForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');

  // Upload Modal State
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', description: '', duration: '' });
  const [uploadVideoFile, setUploadVideoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Message popup state
  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState({ type: '', text: '' });

  const displayMessage = (type, text) => {
    setMessageContent({ type, text });
    setShowMessage(true);
    setTimeout(() => {
      setShowMessage(false);
    }, 3000);
  };

  useEffect(() => {
    fetchReels();
  }, []);

  const fetchReels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // console.log(token);
      // Simulated dummy data in case API fails or is empty, to ensure the UI can be tested
      const dummyReels = [
        {
          id: 1,
          title: 'Amazing Dance Move',
          uploader_name: 'dance_creator',
          description: 'Check out this choreography #dance #clap',
          video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
          likes_count: 124,
          comments_count: 12,
          views_count: 1500
        },
        {
          id: 2,
          title: 'Behind the Scenes',
          uploader_name: 'bts_filmmaker',
          description: 'On the set of our latest short film. #bts #acting',
          video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
          likes_count: 342,
          comments_count: 45,
          views_count: 8900
        }
      ];

      const response = await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/feed', {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.status && data.data && data.data.reels && data.data.reels.length > 0) {
        setReels(data.data.reels);
      } else if (data.status && data.data && data.data.data && data.data.data.length > 0) {
        setReels(data.data.data);
      } else if (data.status && Array.isArray(data.data) && data.data.length > 0) {
        setReels(data.data);
      } else {
        // Fallback to dummy data for display purposes if feed is empty
        setReels(dummyReels);
      }
    } catch (error) {
      // console.error('Error fetching reels:', error);
      // Fallback
      setReels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, clientHeight } = containerRef.current;
    const index = Math.round(scrollTop / clientHeight);
    if (index !== activeReelIndex) {
      setActiveReelIndex(index);
    }
  };

  const openComments = (reel) => {
    setActiveReelForComments(reel);
    setIsCommentsOpen(true);
    fetchComments(reel.id);
  };

  const closeComments = () => {
    setIsCommentsOpen(false);
    setActiveReelForComments(null);
    setComments([]);
  };

  const fetchComments = async (reelId) => {
    try {
      setCommentsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/comments/${reelId}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.status && data.data) {
        setComments(data.data);
      } else {
        setComments([]);
      }
    } catch (e) {
      // console.error('Error fetching comments:', e);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handlePostComment = async () => {
    if (!newCommentText.trim() || !activeReelForComments) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reel_id: activeReelForComments.id,
          comment: newCommentText.trim()
        })
      });
      const data = await response.json();
      if (data.status) {
        displayMessage('success', 'Comment added successfully');
        setNewCommentText('');
        fetchComments(activeReelForComments.id);
        
        // Also update comment count on the reel in the list
        setReels(prevReels => prevReels.map(r => {
          if (r.id === activeReelForComments.id) {
            return {
              ...r,
              comments_count: (parseInt(r.comments_count) || 0) + 1
            };
          }
          return r;
        }));
      } else {
        displayMessage('error', 'Failed to add comment');
      }
    } catch (e) {
      // console.error('Error posting comment:', e);
      displayMessage('error', 'An error occurred. Please try again.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadVideoFile(file);

    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';
    videoElement.src = URL.createObjectURL(file);
    videoElement.onloadedmetadata = () => {
      URL.revokeObjectURL(videoElement.src);
      const seconds = Math.round(videoElement.duration);
      setUploadData(prev => ({
        ...prev,
        duration: seconds.toString()
      }));
    };
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadVideoFile) {
      displayMessage('error', "Please select a video to upload.");
      return;
    }

    try {
      setIsUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('duration', uploadData.duration);
      formData.append('video', uploadVideoFile);

      const response = await fetch('https://www.whysocial.in/clap-kartel-reels-module/public/api/reels/upload', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      if (result.status || response.ok) {
        displayMessage('success', "Reel uploaded successfully!");
        setIsUploadOpen(false);
        setUploadData({ title: '', description: '', duration: '' });
        setUploadVideoFile(null);
        fetchReels(); // Refresh feed
      } else {
        displayMessage('error', result.message || "Failed to upload reel.");
      }
    } catch (error) {
      // console.error("Upload error:", error);
      displayMessage('error', "An error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="reels-page-wrapper">
      {/* Upload Button */}
      <button className="reel-upload-floating-btn" onClick={() => setIsUploadOpen(true)}>
        <FaPlus /> <span>Upload</span>
      </button>

      <div className="reels-container" ref={containerRef} onScroll={handleScroll}>
        {loading && reels.length === 0 ? (
          <div className="reels-loading">Loading Reels...</div>
        ) : reels.length === 0 ? (
          <div className="reels-empty">No reels found.</div>
        ) : (
          reels.map((reel, index) => (
            <div className="reel-snap-point" key={reel.id || index}>
              <ReelVideo
                reel={reel}
                isActive={index === activeReelIndex}
                onOpenComments={openComments}
              />
            </div>
          ))
        )}
      </div>

      {/* Bottom Sheet for Comments */}
      <div className={`comments-bottom-sheet ${isCommentsOpen ? 'open' : ''}`}>
        <div className="comments-sheet-header">
          <h3>Comments {activeReelForComments ? `- ${activeReelForComments.title}` : ''}</h3>
          <button className="close-btn" onClick={closeComments}>
            <IoClose size={24} />
          </button>
        </div>
        <div className="comments-sheet-body">
          {commentsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px', color: '#BF8906', gap: '8px' }}>
              <FaSpinner className="spinner" /> Loading comments...
            </div>
          ) : comments.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#888', padding: '20px' }}>No comments yet</div>
          ) : (
            comments.map((comment, index) => (
              <div className="comment-item" key={comment.id || index}>
                <img 
                  src={comment.user_profile_image || DEFAULT_AVATAR} 
                  alt={comment.user_name} 
                  className="comment-avatar"
                  onError={(e) => { 
                    e.target.src = DEFAULT_AVATAR; 
                    e.target.style.padding = '4px';
                  }}
                  style={{ objectFit: 'cover', padding: !comment.user_profile_image ? '4px' : '0' }}
                />
                <div className="comment-content">
                  <strong>{comment.user_name || 'Anonymous'}</strong>
                  <p>{comment.comment}</p>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="comments-input-area">
          <input 
            type="text" 
            placeholder="Add a comment..." 
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePostComment()}
          />
          <button onClick={handlePostComment} disabled={!newCommentText.trim()}>Post</button>
        </div>
      </div>

      {/* Overlay to close bottom sheet */}
      {isCommentsOpen && (
        <div className="comments-overlay" onClick={closeComments}></div>
      )}

      {/* Upload Modal */}
      {isUploadOpen && (
        <div className="reel-upload-modal-overlay">
          <div className="reel-upload-modal">
            <div className="reel-upload-modal-header">
              <h2>Upload New Reel</h2>
              <button className="close-btn" onClick={() => setIsUploadOpen(false)}>
                <IoClose size={24} />
              </button>
            </div>
            <form className="reel-upload-form" onSubmit={handleUploadSubmit}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  required
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  placeholder="Enter reel title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  required
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  placeholder="Enter description"
                  rows="3"
                ></textarea>
              </div>
              <div className="form-group">
                <label>Duration (seconds)</label>
                <input
                  type="number"
                  required
                  value={uploadData.duration}
                  onChange={(e) => setUploadData({ ...uploadData, duration: e.target.value })}
                  placeholder="e.g. 15"
                />
              </div>
              <div className="form-group">
                <label>Video File</label>
                <input
                  type="file"
                  accept="video/*"
                  required
                  onChange={handleFileChange}
                />
              </div>
              <button type="submit" className="reel-upload-submit-btn" disabled={isUploading}>
                {isUploading ? <><FaSpinner className="spinner" /> Uploading...</> : 'Upload Reel'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Custom Message Popup */}
      <AnimatePresence>
        {showMessage && (
          <motion.div
            className={`message-popup message-${messageContent.type}`}
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <div className="message-icon">
              {messageContent.type === 'success' ? '✓' : '✕'}
            </div>
            <div className="message-text">
              {messageContent.text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reel;
